import { useState } from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { groupMenu } from "@/lib/menuOrder";

function Item({ id, children, onUp, onDown, testid, big }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={testid}
      className={`flex items-center gap-2 rounded-xl border bg-white ${big ? "p-3" : "px-2 py-1.5"} ${isDragging ? "z-10 border-emerald-400 shadow-lg" : "border-slate-200"}`}>
      <button type="button" {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded p-1 text-slate-400 hover:text-slate-700 active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      <div className="min-w-0 flex-1">{children}</div>
      <button type="button" data-testid={`${testid}-up`} onClick={onUp} aria-label="Move up" className="ka-press rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-800"><ChevronUp className="h-4 w-4" /></button>
      <button type="button" data-testid={`${testid}-down`} onClick={onDown} aria-label="Move down" className="ka-press rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-800"><ChevronDown className="h-4 w-4" /></button>
    </div>
  );
}

function SortList({ ids, setIds, render }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const move = (i, j) => j >= 0 && j < ids.length && setIds(arrayMove(ids, i, j));
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => over && active.id !== over.id && setIds(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)))}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">{ids.map((id, i) => render(id, () => move(i, i - 1), () => move(i, i + 1)))}</div>
      </SortableContext>
    </DndContext>
  );
}

function Arranger({ dishes, order, onSaved }) {
  const start = groupMenu(dishes, order);
  const [cats, setCats] = useState(start.map(([c]) => c));
  const [lists, setLists] = useState(Object.fromEntries(start.map(([c, ds]) => [c, ds.map((d) => d.id)])));
  const [busy, setBusy] = useState(false);
  const byId = Object.fromEntries(dishes.map((d) => [d.id, d]));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/allergen-menu/order", { categories: cats, dish_ids: cats.flatMap((c) => lists[c]) });
      toast.success("Menu order saved");
      onSaved(cats);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Drag the handle, or use the arrows, to match your printed menu. Sections move with all their dishes.</p>
      <SortList ids={cats} setIds={setCats} render={(c, up, down) => (
        <Item key={c} id={c} big onUp={up} onDown={down} testid={`arrange-section-${c.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="mb-2 font-display font-bold text-slate-900">{c} <span className="text-xs font-normal text-slate-400">{lists[c].length} dishes</span></div>
          <SortList ids={lists[c]} setIds={(ids) => setLists({ ...lists, [c]: ids })} render={(id, u, d) => (
            <Item key={id} id={id} onUp={u} onDown={d} testid={`arrange-dish-${id}`}><span className="text-sm">{byId[id].name}</span></Item>
          )} />
        </Item>
      )} />
      <button data-testid="arrange-save" disabled={busy} onClick={save} className="ka-press w-full rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Save menu order"}</button>
    </div>
  );
}

export function ArrangeMenuButton({ dishes, order, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="arrange-menu-button" onClick={() => setOpen(true)} disabled={!dishes.length} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold hover:border-slate-900 disabled:opacity-50"><ArrowDownUp className="h-4 w-4" />Arrange menu</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="arrange-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Arrange your menu</DialogTitle></DialogHeader>
          {open && <Arranger dishes={dishes} order={order} onSaved={(cats) => { setOpen(false); onSaved(cats); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
